"use client";

import type { ComedianWithImageUrl, FeedPost } from "./types";
import { SpecialRow, SpecialWithUnlocked } from "@/data/specials";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";

import type { Comedian } from "@/data/comedians";
import { FeedHeader } from "./FeedHeader";
import FeedPostsV2 from "./FeedPostsV2";
import { FilterType } from "./FeedFilter";
import { createClientComponentClient } from "@punchup/data/supabase/client";

interface FeedWrapperProps {
  isLoggedOutState: boolean;
  /**
   * True when the logged-out brand moment is on (feed_brand_moment gate): the
   * hero above owns the page h1 (FeedHeader hides "Your Feed") and the feed
   * ends in a conversion card instead of the dead "no more posts" line.
   */
  brandMomentEnabled?: boolean;
  unloggedFeedPosts: FeedPost[];
  feedPosts: FeedPost[];
  suggestedComedians: ComedianWithImageUrl[];
  comedians: { [comedianId: string]: Comedian };
  initialFilter: FilterType;
  continueWatchingSpecials?: {
    last_playback_location: number;
    special: SpecialRow;
    comedian: Comedian;
  }[];
  trendingSpecials?: SpecialWithUnlocked[];
  showMyComediansFilter?: boolean;
}

export default function FeedWrapperV2({
  isLoggedOutState,
  brandMomentEnabled = false,
  unloggedFeedPosts,
  feedPosts,
  suggestedComedians,
  comedians,
  initialFilter,
  continueWatchingSpecials,
  trendingSpecials,
  showMyComediansFilter = false,
}: FeedWrapperProps) {
  const [activeFilter, setActiveFilter] = useState<FilterType>(initialFilter);
  const supabase = useRef(createClientComponentClient()).current;

  const queryKey = ["feed", activeFilter, isLoggedOutState ? "anon" : "auth"];
  const getStableId = () =>
    document.cookie
      .split("; ")
      .find((c) => c.startsWith("pu_stable_id="))
      ?.split("=")[1] ?? "";

  const fetchFeed = async () => {
    const stableId = getStableId();

    // pull the current session & bearer token
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch(
      `/api/feed-items/v2?filter=${activeFilter}&stableId=${stableId}`,
      {
        credentials: "include",
        cache: "no-store",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!res.ok) throw new Error("failed to fetch feed");
    return res.json();
  };

  // only prime the very first key
  const firstKey = useRef(queryKey.join("-")).current;
  const isFirstRender = firstKey === queryKey.join("-");

  const { data: feedData, isFetching } = useQuery({
    queryKey,
    queryFn: fetchFeed,
    initialData: isFirstRender
      ? {
          //  personalised SSR data
          feedPosts:
            isLoggedOutState && activeFilter === "hot"
              ? unloggedFeedPosts
              : feedPosts,
          suggestedComedians,
        }
      : undefined,
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnMount: !isLoggedOutState ? "always" : true,
    refetchOnWindowFocus: false,
  });

  const filteredPosts = feedData?.feedPosts ?? [];
  const serverSuggested = feedData?.suggestedComedians ?? [];

  return (
    <>
      <FeedHeader
        isLoggedOut={isLoggedOutState}
        hideTitle={brandMomentEnabled}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        showMyComediansFilter={showMyComediansFilter}
      />
      <div className="flex flex-col gap-6 pt-4">
        <FeedPostsV2
          isLoggedOutState={isLoggedOutState}
          brandMomentEnabled={brandMomentEnabled}
          feedPosts={filteredPosts}
          suggestedComedians={serverSuggested}
          comedians={comedians}
          isLoading={isFetching}
          continueWatchingSpecials={continueWatchingSpecials}
          trendingSpecials={trendingSpecials}
        />
      </div>
    </>
  );
}
